import {
    FC,
    ReactElement,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useSearchParams } from 'react-router'
import classNames from 'classnames'

import { LoadingSpinner, TableCell, TableRow } from '../../../components'
import { SortValkTable } from '../../../compositions'
import IconButton from '../../../compositions/@buttons/IconButton/IconButton'
import { SortLabelInfo } from '../../../compositions/@tables/SortLabel/SortLabel'
import { Department } from '../../../entities/Department/Department'
import { sortDepartments } from '../../../entities/Department/DepartmentHelpers'
import trans from '../../../helpers/trans'
import { SortConfig, SortDirection, SortField } from '../../../types/tableTypes'

import './DepartmentsTable.scss'

interface DepartmentsTableProps {
    isLoading?: boolean;
    loadingDepartmentIds?: string[];
    canDeleteDepartment?: boolean;
    canEditDepartment?: boolean;
    departments: Department[];
    onDeleteDepartmentClick?: (department: Department) => void;
    onEditDepartmentClick?: (department: Department) => void;
    className?: string;
}

const DepartmentsTable: FC<DepartmentsTableProps> = ({
    isLoading,
    loadingDepartmentIds,
    canDeleteDepartment,
    canEditDepartment,
    departments,
    onDeleteDepartmentClick,
    onEditDepartmentClick,
    className = '',
}): ReactElement => {
    const [searchParams] = useSearchParams()
    const activeDepartmentId = searchParams.get('id')

    const activeRowRef = useRef<HTMLTableRowElement | null>(null)

    const activeDepartment = useMemo(
        () => departments.find(department => department.id === activeDepartmentId) ?? null,
        [departments, activeDepartmentId],
    )

    const [sortConfig, setSortConfig] = useState<SortConfig>({ field: SortField.group, direction: SortDirection.desc })

    const sortedDepartments = useMemo(() => sortDepartments([...departments], sortConfig.field, sortConfig.direction), [departments, sortConfig.field, sortConfig.direction])

    const sortCells: SortLabelInfo[] = [
        { label: trans('common.group'), field: SortField.group },
        { label: trans('common.department'), field: SortField.department },
        { label: '' },
    ]

    const handleTableSort = (field: SortField = SortField.name, direction = SortDirection.desc): void => {
        setSortConfig({ field, direction })
    }

    useEffect(() => {
        if (activeDepartment) {
            onEditDepartmentClick?.(activeDepartment)
            activeRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
    }, [activeDepartmentId])

    return (
        <div className={`departments-table ${className}`}>
            <SortValkTable<Department>
                rows={sortedDepartments}
                sortCells={sortCells}
                sortConfig={sortConfig}
                onSort={handleTableSort}
                className="departments-table__valk-table"
                cellClassName="departments-table__cell"
                cellContentClassName="departments-table__cell-content"
                tableClassName="departments-table__table"
            >
                {(departmentRows: Department[]): ReactElement[] => departmentRows.map(department => {
                    const isActive = department.id === activeDepartmentId

                    return (
                        <TableRow
                            key={department.id}
                            ref={isActive ? activeRowRef : undefined}
                            loading={loadingDepartmentIds?.includes(department.id)}
                            className={classNames({ 'departments-table__row--is-active': isActive })}
                        >
                            <TableCell className="departments-table__cell">
                                {department.departmentGroup?.name}
                            </TableCell>

                            <TableCell className="departments-table__cell">
                                {department.name}
                            </TableCell>

                            <TableCell className="departments-table__buttons-cell">
                                {(canEditDepartment && onEditDepartmentClick) && (
                                    <IconButton
                                        hideLabel
                                        onClick={() => onEditDepartmentClick(department)}
                                        icon="edit"
                                        text="edit"
                                        className="departments-table__button"
                                    />
                                )}

                                {(canDeleteDepartment && onDeleteDepartmentClick) && (
                                    <IconButton
                                        hideLabel
                                        onClick={() => onDeleteDepartmentClick(department)}
                                        icon="trash"
                                        text="delete"
                                        className="departments-table__button"
                                    />
                                )}
                            </TableCell>
                        </TableRow>
                    )
                })}
            </SortValkTable>

            {isLoading && <LoadingSpinner className="departments-table__loading-spinner" />}
        </div>
    )
}

export default DepartmentsTable
